import UrlParser from 'url-parse'
import promisify from './promisify'
import timeFormat from './timeFormat'
import timeFrom from './timeFrom'
import debounce from './debounce'
import throttle from './throttle'
import deepClone from './deepClone'
import deepMerge from './deepMerge'
import { parseUrlParams, serializeUrlParams } from './urlParams'
import trim from './trim'
import random from './random'
import { colorGradient, hexToRgb, rgbToHex, colorToRgba } from './colorCalculate'

const Utils = {
  UrlParser: UrlParser,
  _typeof(obj) {
    return Object.prototype.toString
      .call(obj)
      .slice(8, -1)
      .toLowerCase()
  },
  isPlainObject(obj) {
    return Utils._typeof(obj) === 'object'
  },
  isString(obj) {
    return typeof obj === 'string'
  },
  isNonEmptyArray(obj = []) {
    return obj && obj.length > 0 && Array.isArray(obj) && typeof obj !== 'undefined'
  },
  isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item)
  },
  isEmptyObject(obj) {
    return Object.keys(obj).length === 0 && obj.constructor === Object
  },
  decodeIconFont(text) {
    // 正则匹配 图标和文字混排 eg: 我去上学校&#xe600;,天天不&#xe600;迟到
    const regExp = /&#x[a-z|0-9]{4,5};?/g
    if (regExp.test(text)) {
      return text.replace(new RegExp(regExp, 'g'), function(iconText) {
        const replace = iconText.replace(/&#x/, '0x').replace(/;$/, '')
        return String.fromCharCode(replace)
      })
    } else {
      return text
    }
  },
  mergeDeep(target, ...sources) {
    if (!sources.length) return target
    const source = sources.shift()
    if (Utils.isObject(target) && Utils.isObject(source)) {
      for (const key in source) {
        if (Utils.isObject(source[key])) {
          if (!target[key]) {
            Object.assign(target, {
              [key]: {}
            })
          }
          Utils.mergeDeep(target[key], source[key])
        } else {
          Object.assign(target, { [key]: source[key] })
        }
      }
    }
    return Utils.mergeDeep(target, ...sources)
  },
  appendProtocol(url) {
    if (/^\/\//.test(url)) {
      const { bundleUrl } = weex.config
      return `http${/^https:/.test(bundleUrl) ? 's' : ''}:${url}`
    }
    return url
  },
  encodeURLParams(url) {
    const parsedUrl = new UrlParser(url, true)
    return parsedUrl.toString()
  },
  goToH5Page(jumpUrl, animated = false, callback = null) {
    const Navigator = weex.requireModule('navigator')
    const jumpUrlObj = new Utils.UrlParser(jumpUrl, true)
    const url = Utils.appendProtocol(jumpUrlObj.toString())
    Navigator.push(
      {
        url: Utils.encodeURLParams(url),
        animated: animated.toString()
      },
      callback
    )
  },
  env: {
    isWeb() {
      const { platform } = weex.config.env
      return typeof window === 'object' && platform.toLowerCase() === 'web'
    },
    isHarmony() {
      const { platform } = weex.config.env
      return platform.toLowerCase() === 'harmony'
    },
    isIOS() {
      const { platform } = weex.config.env
      return platform.toLowerCase() === 'ios'
    },
    /**
     * 是否为 iPhone X(2436) or iPhoneXS(2436) or iPhoneXR(1792) or iPhoneXS Max(2688)
     * 是否为 iphone 11(1792) or iphone 11 pro(2436) or iphone 11 pro max(2688)
     * 是否为 iphone 12(2532) or iphone 12 Mini(2340) or iphone 12(2532) or iphone 12(2778)
     * @returns {boolean}
     */
    isIPhoneX() {
      const { deviceHeight } = weex.config.env
      if (Utils.env.isWeb()) {
        return (
          typeof window !== undefined &&
          window.screen &&
          window.screen.width &&
          window.screen.height &&
          ((parseInt(window.screen.width, 10) === 375 && parseInt(window.screen.height, 10) === 812) ||
            (parseInt(window.screen.width, 10) === 414 && parseInt(window.screen.height, 10) === 896))
        )
      }
      return (
        Utils.env.isIOS() &&
        (weex.config.env.isFullScreen ||
        deviceHeight === 2436 || // 11pro, x, xs
        deviceHeight === 2688 || // xs max, 11pro max
        deviceHeight === 1792 || // 11, xr
        deviceHeight === 1624 ||
        deviceHeight === 2532 || // 12, 12pro
        deviceHeight === 2556 || // 14pro
        deviceHeight === 2340 || // 12mini
        deviceHeight === 2796 || // 14pro Max
          deviceHeight === 2778) // 12Pro Max
      )
    },
    /**
     * ios是否是全面屏设备，黑名单方式
     * 除了 iphoneXR iphone11 属于1792，其余都大于 1920，
     * 经测试ip 8 plus 原生返回的是2208，非1920有点问题，所以改成deviceModel判断
     * @returns {boolean}
     */
    // isNotch() {
    //   const { deviceHeight } = weex.config.env
    //   if (Utils.env.isWeb()) {
    //     return (
    //       typeof window !== undefined &&
    //       window.screen &&
    //       window.screen.width &&
    //       window.screen.height &&
    //       ((parseInt(window.screen.width, 10) === 375 && parseInt(window.screen.height, 10) === 812) ||
    //         (parseInt(window.screen.width, 10) === 414 && parseInt(window.screen.height, 10) === 896))
    //     )
    //   }
    //   return Utils.env.isIOS() && (deviceHeight > 1920 || deviceHeight === 1792)
    // },
    isNotch() {
      const { platform, deviceModel } = weex.config.env
      const notNotch = [
        'iPhone1,1',
        'iPhone1,2',
        'iPhone3,2',
        'iPhone3,3',
        'iPhone4,1',
        'iPhone5,1',
        'iPhone5,2',
        'iPhone5,3',
        'iPhone5,4',
        'iPhone6,1',
        'iPhone6,2',
        'iPhone7,1',
        'iPhone7,2',
        'iPhone8,1',
        'iPhone8,2',
        'iPhone9,1',
        'iPhone9,2',
        'iPhone9,3',
        'iPhone9,4',
        'iPhone10,1',
        'iPhone10,2',
        'iPhone10,4',
        'iPhone10,5',
        'iPhone12,8',
        'iPhone'
      ]
      const isNotch = !notNotch.includes(deviceModel)
      if (typeof window === 'object' && platform.toLowerCase() === 'web') {
        return (
          typeof window !== undefined &&
          window.screen &&
          window.screen.width &&
          window.screen.height &&
          ((parseInt(window.screen.width, 10) === 375 && parseInt(window.screen.height, 10) === 812) ||
            (parseInt(window.screen.width, 10) === 414 && parseInt(window.screen.height, 10) === 896))
        )
      }
      return platform.toLowerCase() === 'ios' && isNotch
    },
    isAndroid() {
      const { platform } = weex.config.env
      return platform.toLowerCase() === 'android'
    },
    isBuGu() {
      const { appName } = weex.config.env
      return appName === 'BG'
    },
    isMeiJu() {
      const { appName } = weex.config.env
      return /(mj|meiju|美居)/i.test(appName)
    },
    isMideaWeex() {
      return Utils.env.isMeiJu() || Utils.env.isBuGu()
    },
    /**
     * 获取状态栏高度
     */
    statusBarHeight() {
      let result = '20'
      if (weex.config.env.statusBarHeight) {
        if (weex.config.env.platform === 'iOS') {
          // iOS使用pt为单位
          result = weex.config.env.statusBarHeight
        } else {
          // 安卓使用px为单位
          result = weex.config.env.statusBarHeight / weex.config.env.scale
        }
      }
      return result
    },
    navHeight() {
      const { env } = weex.config
      return Utils.env.isWeb() ? 0 : Utils.env.isIPhoneX() ? 176 : 132
    },
    /**
     * 获取页面区域高度 = 设备屏幕实际高度 - 导航栏高度
     * @returns {Number}
     */
    getPageHeight() {
      const { env } = weex.config
      const navHeight = Utils.env.isWeb() ? 0 : Utils.env.isIPhoneX() ? 176 : 132
      return (env.deviceHeight / env.deviceWidth) * 750 - navHeight
    },
    /**
     * 获取设备屏幕实际高度
     * @returns {Number}
     */
    getScreenHeight() {
      const { env } = weex.config
      return (env.deviceHeight / env.deviceWidth) * 750
    }
  },

  /**
   * 版本号比较
   * @memberOf Utils
   * @param currVer {string}
   * @param promoteVer {string}
   * @returns {boolean}
   * @example
   *
   * const { Utils } = require('@ali/wx-bridge');
   * const { compareVersion } = Utils;
   * console.log(compareVersion('0.1.100', '0.1.11')); // 'true'
   */
  compareVersion(currVer = '0.0.0', promoteVer = '0.0.0') {
    if (currVer === promoteVer) return true
    const currVerArr = currVer.split('.')
    const promoteVerArr = promoteVer.split('.')
    const len = Math.max(currVerArr.length, promoteVerArr.length)
    for (let i = 0; i < len; i++) {
      const proVal = ~~promoteVerArr[i]
      const curVal = ~~currVerArr[i]
      if (proVal < curVal) {
        return true
      } else if (proVal > curVal) {
        return false
      }
    }
    return false
  },
  /**
   * 分割数组
   * @param arr 被分割数组
   * @param size 分割数组的长度
   * @returns {Array}
   */
  arrayChunk(arr = [], size = 4) {
    let groups = []
    if (arr && arr.length > 0) {
      groups = arr
        .map((e, i) => {
          return i % size === 0 ? arr.slice(i, i + size) : null
        })
        .filter(e => {
          return e
        })
    }
    return groups
  },
  /*
   * 截断字符串
   * @param str 传入字符串
   * @param len 截断长度
   * @param hasDot 末尾是否...
   * @returns {String}
   */
  truncateString(str, len, hasDot = true) {
    let newLength = 0
    let newStr = ''
    let singleChar = ''
    const chineseRegex = /[^\x00-\xff]/g
    const strLength = str.replace(chineseRegex, '**').length
    for (let i = 0; i < strLength; i++) {
      singleChar = str.charAt(i).toString()
      if (singleChar.match(chineseRegex) !== null) {
        newLength += 2
      } else {
        newLength++
      }
      if (newLength > len) {
        break
      }
      newStr += singleChar
    }

    if (hasDot && strLength > len) {
      newStr += '...'
    }
    return newStr
  },
  /**
   * 获取页面参数(bundleJS),从url查询参数中获取
   * @method parseUrlParams
   * @return {object} 返回json数据
   */
  parseUrlParams() {
    const params = {}
    const url = weex.config.bundleUrl
    const index = url.indexOf('?')
    if (index > 0) {
      const query = url.substring(index + 1)
      const temp = query.split('&')
      let key, value
      for (var p in temp) {
        if (temp[p]) {
          key = temp[p].split('=')[0]
          value = temp[p].split('=')[1]
          params[key] = decodeURIComponent(value)
        }
      }
    }
    return params
  },
  /*
   * 转换 obj 为 url params参数
   * @param obj 传入字符串
   * @returns {String}
   */
  objToParams(obj) {
    let str = ''
    for (const key in obj) {
      if (str !== '') {
        str += '&'
      }
      str += key + '=' + encodeURIComponent(obj[key])
    }
    return str
  },
  /*
   * 转换 url params参数为obj
   * @param str 传入url参数字符串
   * @returns {Object}
   */
  paramsToObj(str) {
    let obj = {}
    try {
      obj = JSON.parse(
        '{"' +
          decodeURI(str)
            .replace(/"/g, '\\"')
            .replace(/&/g, '","')
            .replace(/=/g, '":"') +
          '"}'
      )
    } catch (e) {
      console.log(e)
    }
    return obj
  },
  animation: {
    /**
     * 返回定义页面转场动画起初的位置
     * @param ref
     * @param transform 运动类型
     * @param status
     * @param callback 回调函数
     */
    pageTransitionAnimation(ref, transform, status, callback) {
      const animation = weex.requireModule('animation')
      animation.transition(
        ref,
        {
          styles: {
            transform: transform
          },
          duration: status ? 250 : 300, // ms
          timingFunction: status ? 'ease-in' : 'ease-out',
          delay: 0 // ms
        },
        function() {
          callback && callback()
        }
      )
    }
  },
  uiStyle: {
    /**
     * 返回定义页面转场动画起初的位置
     * @param animationType 页面转场动画的类型 push，model
     * @param size 分割数组的长度
     * @returns {}
     */
    pageTransitionAnimationStyle(animationType) {
      if (animationType === 'push') {
        return {
          left: '750px',
          top: '0px',
          height: (weex.config.env.deviceHeight / weex.config.env.deviceWidth) * 750 + 'px'
        }
      } else if (animationType === 'model') {
        return {
          top: (weex.config.env.deviceHeight / weex.config.env.deviceWidth) * 750 + 'px',
          left: '0px',
          height: (weex.config.env.deviceHeight / weex.config.env.deviceWidth) * 750 + 'px'
        }
      }
      return {}
    }
  }
}

export default Object.assign(Utils, {
  promisify,
  timeFormat,
  timeFrom,
  debounce,
  throttle,
  deepClone,
  deepMerge,
  parseUrlParams,
  serializeUrlParams,
  trim,
  random,
  colorGradient,
  hexToRgb,
  rgbToHex,
  colorToRgba
})

export function oneOf(value, validList) {
  for (let i = 0; i < validList.length; i++) {
    if (value === validList[i]) {
      return true
    }
  }
  return false
}



// WEBPACK FOOTER //
// ./packages/utils/index.js